const FIELD_LABEL_OVERRIDES: Record<string, string> = {
  customerDuns: "Customer DUNS",
  industryOther: "Other Industry",
  awsAccountId: "AWS Account ID",
  partnerProjectTitle: "Project Title",
  otherSolOffered: "Other Solution Offered",
  otherSolutionOffered: "Other Solution Offered",
  estimatedAwsMonthlyReccuringRevenue: "Estimated AWS Monthly Recurring Revenue",
  awsFundingUsed: "AWS Funding Used",
  marketingActivityUseCase: "Marketing Activity Use Cases",
  awsProducts: "AWS Products",
  partnerPrimaryNeedFromAws: "Partner Primary Needs From AWS",
  deliveryModel: "Delivery Models",
  apnPrograms: "APN Programs",
  doesOppBelongToNatSec: "Does Opportunity Belong to National Security?",
  reviewStatus: "AWS Review Status",
  status: "AWS Review Status",
  awsStage: "AWS Stage",
  parentOppId: "Parent Opportunity ID",
  origin: "Origin",
  expectedCustomerSpendCurrencyCode: "Expected Customer Spend Currency Code",
  expectedCustomerSpendFrequency: "Expected Customer Spend Frequency",
  expectedCustomerSpendTargetCompany: "Expected Customer Spend Target Company",
};

const AWS_FIELD_TO_ERROR_KEY: Record<string, string> = {
  "customer.account.companyName": "customerCompanyName",
  "customer.account.duns": "customerDuns",
  "customer.account.industry": "industryVertical",
  "customer.account.otherIndustry": "industryOther",
  "customer.website": "website",
  "customer.account.websiteUrl": "website",
  "customer.account.awsAccountId": "awsAccountId",
  "customer.account.address.streetAddress": "streetAddress",
  "customer.account.address.city": "city",
  "customer.account.address.stateOrRegion": "state",
  "customer.account.address.countryCode": "country",
  "customer.account.address.postalCode": "postalCode",
  "project.title": "partnerProjectTitle",
  "opportunity.project.title": "partnerProjectTitle",
  "project.customerBusinessProblem": "customerBusinessProblem",
  "opportunity.project.customerBusinessProblem": "customerBusinessProblem",
  "project.othersolutiondescription": "otherSolOffered",
  "opportunity.project.othersolutiondescription": "otherSolOffered",
  "project.customerUseCase": "useCase",
  "opportunity.project.customerUseCase": "useCase",
  "project.deliveryModels": "deliveryModel",
  "opportunity.project.deliveryModels": "deliveryModel",
  "project.salesActivities": "salesActivities",
  "opportunity.project.salesActivities": "salesActivities",
  "project.expectedCustomerSpend.amount": "estimatedAwsMonthlyReccuringRevenue",
  "project.expectedCustomerSpend.currencyCode": "expectedCustomerSpendCurrencyCode",
  "opportunity.project.expectedCustomerSpend.currencyCode": "expectedCustomerSpendCurrencyCode",
  "project.expectedCustomerSpend.frequency": "expectedCustomerSpendFrequency",
  "opportunity.project.expectedCustomerSpend.frequency": "expectedCustomerSpendFrequency",
  "project.expectedCustomerSpend.targetCompany": "expectedCustomerSpendTargetCompany",
  "opportunity.project.expectedCustomerSpend.targetCompany": "expectedCustomerSpendTargetCompany",
  "opportunity.origin": "origin",
  "project.otherCompetitorNames": "otherCompetitors",
  "opportunity.project.otherCompetitorNames": "otherCompetitors",
  "project.relatedOpportunityIdentifier": "parentOppId",
  "opportunity.project.relatedOpportunityIdentifier": "parentOppId",
  "opportunity.marketing.source": "marketingSource",
  "opportunity.marketing.awsFundingUsed": "awsFundingUsed",
  "opportunity.marketing.campaignName": "marketingCampaign",
  "opportunity.marketing.channels": "marketingActivityChannel",
  "opportunity.marketing.useCases": "marketingActivityUseCase",
  "opportunity.lifecycle.targetCloseDate": "targetCloseDate",
  "opportunity.lifecycle.nextSteps": "nextSteps",
  "opportunity.lifecycle.closedLostReason": "closedLostReason",
  "opportunity.lifecycle.reviewStatus": "reviewStatus",
  "opportunity.lifecycle.stage": "awsStage",
  stage: "awsStage",
  "lifeCycle.reviewStatus": "reviewStatus",
  "lifeCycle.stage": "awsStage",
  status: "reviewStatus",
  "opportunity.primaryNeedsFromAws": "partnerPrimaryNeedFromAws",
};

const resolveFieldKeyByPath = (field: string): string => {
  if (field === "otherSolutionOffered") return "otherSolOffered";
  const contactFirstNameMatch = field.match(/^customer\.contacts\.(\d+)\.member\.firstName$/);
  if (contactFirstNameMatch) return `contact-${contactFirstNameMatch[1]}-firstName`;
  const contactLastNameMatch = field.match(/^customer\.contacts\.(\d+)\.member\.lastName$/);
  if (contactLastNameMatch) return `contact-${contactLastNameMatch[1]}-lastName`;
  const contactEmailMatch = field.match(/^customer\.contacts\.(\d+)\.member\.email$/);
  if (contactEmailMatch) return `contact-${contactEmailMatch[1]}-email`;
  const contactBusinessTitleMatch = field.match(/^customer\.contacts\.(\d+)\.member\.businessTitle$/);
  if (contactBusinessTitleMatch) return `contact-${contactBusinessTitleMatch[1]}-businessTitle`;
  const contactPhoneMatch = field.match(/^customer\.contacts\.(\d+)\.member\.phone$/);
  if (contactPhoneMatch) return `contact-${contactPhoneMatch[1]}-phone`;
  return AWS_FIELD_TO_ERROR_KEY[field] ?? field;
};

const resolveFieldKeyFromMessage = (message: string): string | undefined => {
  if (!message) return undefined;
  if (/customer website/i.test(message) && /valid domain/i.test(message)) return "website";

  const fieldPathMatch = message.match(/\b(?:customer|project|opportunity)\.[A-Za-z0-9.]+\b/);
  if (fieldPathMatch) {
    const resolvedField = resolveFieldKeyByPath(fieldPathMatch[0]);
    if (resolvedField && resolvedField !== fieldPathMatch[0]) return resolvedField;
  }

  return undefined;
};

const resolveFieldKey = (field: string, message = ""): string => {
  const resolvedField = resolveFieldKeyByPath(field);
  if (resolvedField !== field) return resolvedField;
  return resolveFieldKeyFromMessage(message) ?? resolvedField;
};

const humanizeFieldPath = (value: string): string =>
  value
    .split(".")
    .map((segment) =>
      segment
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/^./, (char) => char.toUpperCase())
    )
    .join(" > ");

const getFieldLabel = (field: string): string => {
  if (FIELD_LABEL_OVERRIDES[field]) return FIELD_LABEL_OVERRIDES[field];
  if (/^contact-\d+(-.+)?$/.test(field)) return "Customer Contacts";
  if (field.includes(".")) return humanizeFieldPath(field);
  return field
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/^./, (char) => char.toUpperCase());
};

export const getOpportunityFieldLabel = getFieldLabel;

export const formatOpportunityFieldMessage = (field: string, message: string): string => {
  const trimmedMessage = String(message || "").trim();
  if (!trimmedMessage) return "";

  const resolvedField = resolveFieldKey(field, trimmedMessage);
  const label = getFieldLabel(resolvedField);
  if (!label) return trimmedMessage;
  if (trimmedMessage.toLowerCase().startsWith(label.toLowerCase())) return trimmedMessage;
  return `${label}: ${trimmedMessage}`;
};

export const normalizeOpportunityErrorMessages = (
  value: string[] | string | null | undefined
): string[] => {
  if (Array.isArray(value)) {
    return value.filter((message): message is string => typeof message === "string" && message.trim() !== "");
  }

  if (typeof value === "string" && value.trim() !== "") {
    return [value];
  }

  return [];
};

export const buildOpportunityValidationSummary = (
  errors: Record<string, string>,
  apiError: string[] | string | null | undefined
): string[] => {
  const fieldMessages = Object.entries(errors)
    .filter(([field, message]) => field !== "_general" && typeof message === "string" && message.trim() !== "")
    .map(([field, message]) => formatOpportunityFieldMessage(field, message));

  return Array.from(new Set([...fieldMessages, ...normalizeOpportunityErrorMessages(apiError)]));
};

export const mapOpportunityApiErrors = (
  resp: any
): { apiError: string[]; fieldErrors: Record<string, string> } => {
  const list = resp?.response?.ErrorList || resp?.ErrorList;
  if (!list) return { apiError: [], fieldErrors: {} };

  const fieldErrors: Record<string, string> = {};
  const apiError = list.map((entry: any) => {
    const rawFieldName = typeof entry?.FieldName === "string" ? entry.FieldName : "";
    const baseMessage = String(entry?.Message || entry?.Code || "Unknown error").trim();
    const resolvedField = resolveFieldKey(rawFieldName, baseMessage);
    const formattedMessage = formatOpportunityFieldMessage(resolvedField, baseMessage);

    if (resolvedField && !resolvedField.includes(".")) {
      fieldErrors[resolvedField] = baseMessage;
    }

    return formattedMessage || baseMessage;
  });

  return { apiError, fieldErrors };
};

export const collectOpportunityBusinessRuleMessages = (
  ruleErrors: Record<string, string> | undefined
): string[] => {
  if (!ruleErrors) return [];

  const messages = Object.entries(ruleErrors)
    .filter(([field]) => field !== "_general")
    .map(([field, value]) => formatOpportunityFieldMessage(field, String(value || "")))
    .filter(Boolean);

  return Array.from(new Set(messages));
};
